import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Images, HardDrive, Clock, Server, ArrowUpRight,
  Upload, Settings, ExternalLink, Calendar, RefreshCw, Video, Image
} from 'lucide-react';

const formatBytes = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [r2Usage, setR2Usage] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, photosRes, bucketsRes] = await Promise.all([
          axios.get('/api/storage/stats'),
          axios.get('/api/user/photos'),
          axios.get('/api/buckets'),
        ]);
        setStats(statsRes.data);
        setPhotos(photosRes.data);
        setBuckets(bucketsRes.data);

        // Fetch real R2 usage
        try {
          const r2Res = await axios.get('/api/storage/r2-usage');
          setR2Usage(r2Res.data);
        } catch { /* R2 usage unavailable */ }
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const refreshUsage = async () => {
    setRefreshing(true);
    try {
      const [statsRes, r2Res] = await Promise.all([
        axios.get('/api/storage/stats'),
        axios.get('/api/storage/r2-usage'),
      ]);
      setStats(statsRes.data);
      setR2Usage(r2Res.data);
    } catch { /* silent */ } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="skeleton h-20 rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-28 rounded-xl" />
          ))}
        </div>
        <div className="skeleton h-48 rounded-xl" />
        <div className="skeleton h-64 rounded-xl" />
      </div>
    );
  }

  // Use R2 data if available, fallback to in-memory stats
  const totalSize = r2Usage?.totalSize ?? stats?.totalSize ?? 0;
  const totalCount = r2Usage?.totalCount ?? stats?.totalCount ?? 0;

  const cards = [
    {
      label: 'Photos',
      value: stats?.photoCount ?? 0,
      icon: Image,
      lightBg: 'stat-icon-rose',
      textColor: '',
    },
    {
      label: 'Videos',
      value: stats?.videoCount ?? 0,
      icon: Video,
      lightBg: 'stat-icon-purple',
      textColor: '',
    },
    {
      label: 'Storage Used',
      value: formatBytes(totalSize),
      icon: HardDrive,
      lightBg: 'stat-icon-emerald',
      textColor: '',
    },
    {
      label: 'R2 Buckets',
      value: buckets.length,
      icon: Server,
      lightBg: 'stat-icon-amber',
      textColor: '',
    },
  ];

  const recentPhotos = [...photos]
    .sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))
    .slice(0, 8);

  const quickActions = [
    { label: 'Upload', icon: Upload, to: '/dashboard/upload', color: 'bg-primary-container text-on-primary-container hover:bg-primary-container/90 shadow-bubble' },
    { label: 'Photos', icon: Images, to: '/dashboard/photos', color: 'bg-card border border-border text-foreground hover:bg-muted' },
    { label: 'Settings', icon: Settings, to: '/dashboard/settings', color: 'bg-card border border-border text-foreground hover:bg-muted' },
    { label: 'View Album', icon: ExternalLink, to: '/album', color: 'bg-card border border-border text-foreground hover:bg-muted' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ─── Welcome Card ─── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/90 p-6 sm:p-8 text-primary-foreground shadow-soft">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
        <div className="absolute top-4 right-[25%] w-6 h-6 bg-white/10 rounded-full float-gentle" />
        <div className="absolute bottom-6 left-[35%] w-4 h-4 bg-white/8 rounded-full float-gentle-delayed" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={14} className="text-primary-foreground/60" />
            <span className="text-xs text-primary-foreground/60 font-medium">
              {new Date().toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
            Welcome back, Sopia
          </h2>
          <p className="text-primary-foreground/70 text-sm mt-1.5 max-w-md">
            {totalCount > 0
              ? `${stats?.photoCount ?? 0} photo${(stats?.photoCount ?? 0) !== 1 ? 's' : ''}, ${stats?.videoCount ?? 0} video${(stats?.videoCount ?? 0) !== 1 ? 's' : ''} stored across ${buckets.length} bucket${buckets.length !== 1 ? 's' : ''} (${formatBytes(totalSize)} total).`
              : 'Get started by uploading your first photo or video.'}
          </p>
          <div className="flex items-center gap-2 mt-5 flex-wrap">
            {quickActions.map((a) => (
              <Link
                key={a.label}
                to={a.to}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold transition-all duration-200 active:scale-[0.97] ${a.color}`}
                {...(a.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                <a.icon size={14} />
                {a.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Stat Cards ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 stagger">
        {cards.map((c) => (
          <div key={c.label} className="card-hover p-4 sm:p-5 group cursor-default">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${c.lightBg} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                <c.icon size={18} />
              </div>
            </div>
            <p className="font-heading font-extrabold text-2xl text-foreground tracking-tight tabular-nums">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5 font-medium">{c.label}</p>
          </div>
        ))}
      </div>

      {/* ─── R2 Storage Usage ─── */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-sm text-foreground">R2 Storage Usage</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalCount > 0
                ? `${totalCount} object${totalCount !== 1 ? 's' : ''} across ${buckets.length} bucket${buckets.length !== 1 ? 's' : ''}`
                : 'No objects in R2 buckets'}
            </p>
          </div>
          <button
            onClick={refreshUsage}
            disabled={refreshing}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer"
            aria-label="Refresh usage"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Per-bucket breakdown */}
        {buckets.length > 0 ? (
          <div className="space-y-4">
            {buckets.map((b) => {
              const usage = r2Usage?.perBucket?.[b.id];
              const bucketSize = usage?.size || 0;
              const bucketCount = usage?.count || 0;
              const pct = totalSize > 0 ? (bucketSize / totalSize) * 100 : 0;

              return (
                <div key={b.id}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                      <span className="text-sm font-medium text-foreground">{b.name}</span>
                      {usage?.error && (
                        <span className="text-[10px] text-red-500 font-medium">Connection error</span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground font-mono tabular-nums">
                      {bucketCount} object{bucketCount !== 1 ? 's' : ''} - {formatBytes(bucketSize)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(pct, bucketCount > 0 ? 1 : 0)}%`,
                        background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center">
            <p className="text-muted-foreground text-sm font-medium">No buckets configured.</p>
            <Link to="/dashboard/settings" className="text-xs font-bold text-primary hover:text-primary/80 mt-1 inline-block transition-colors">
              Add a bucket
            </Link>
          </div>
        )}

        {/* Total */}
        {buckets.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Total</span>
            <span className="text-sm font-heading font-bold text-primary tabular-nums">{formatBytes(totalSize)}</span>
          </div>
        )}
      </div>

      {/* ─── Recent Photos ─── */}
      {recentPhotos.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-sm text-foreground">Recent Photos</h3>
            <Link to="/dashboard/photos" className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1 transition-colors">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-2.5 stagger">
            {recentPhotos.map((p) => (
              <Link
                key={p.id}
                to="/dashboard/photos"
                className="aspect-square rounded-2xl overflow-hidden bg-muted group relative shadow-xs hover:shadow-soft-card transition-all duration-300 hover:scale-[1.03] squishy-hover"
              >
                <img
                  src={p.url}
                  alt={p.filename}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23F7F3FD" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%237C3AED" font-size="14">IMG</text></svg>';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ─── Empty State ─── */}
      {recentPhotos.length === 0 && photos.length === 0 && (
        <div className="card py-16 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-primary/10">
            <Images size={28} className="text-primary/60" />
          </div>
          <h3 className="font-heading font-bold text-base text-foreground mb-1">No photos yet</h3>
          <p className="text-muted-foreground text-sm max-w-xs mx-auto mb-5">
            Upload your first photo to get started.
          </p>
          <Link to="/dashboard/upload" className="btn-primary btn-sm inline-flex">
            <Upload size={14} /> Upload Photo
          </Link>
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;
